interface LLMResponse {
  choices: Array<{
    message: {
      content: string;
    };
  }>;
}

interface LLMRequest {
  model: string;
  messages: Array<{
    role: 'system' | 'user' | 'assistant';
    content: string;
  }>;
  temperature?: number;
  max_tokens?: number;
}

class LLMService {
  private apiKey = import.meta.env.VITE_GROQ_API_KEY || 'gsk_demo_key_for_testing';
  private baseUrl = 'https://api.groq.com/openai/v1';

  async generateResponse(userMessage: string, language: string = 'en-US'): Promise<string> {
    try {
      const systemPrompt = this.getSystemPrompt(language);
      
      const request: LLMRequest = {
        model: 'meta-llama/llama-4-scout-17b-16e-instruct',
        messages: [
          {
            role: 'system',
            content: systemPrompt
          },
          {
            role: 'user',
            content: userMessage
          }
        ],
        temperature: 0.7,
        max_tokens: 500
      };

      const response = await fetch(`${this.baseUrl}/chat/completions`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${this.apiKey}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(request)
      });

      if (!response.ok) {
        throw new Error(`LLM API error: ${response.status}`);
      }

      const data: LLMResponse = await response.json();
      
      if (data.choices && data.choices.length > 0) {
        return data.choices[0].message.content;
      }
      
      throw new Error('No response from LLM');
    } catch (error) {
      console.error('LLM Service Error:', error);
      return this.getFallbackResponse(language);
    }
  }

  private getSystemPrompt(language: string): string {
    const prompts: Record<string, string> = {
      'en-US': `You are a helpful voice payment assistant for a BNB Chain cryptocurrency wallet application. 
        You help users with:
        - Sending BNB and other tokens
        - Checking wallet balances
        - Viewing transaction history
        - Understanding blockchain transactions
        
        Always be concise, helpful, and security-conscious. 
        Never ask for private keys or seed phrases.
        Respond in a conversational tone suitable for voice interaction.
        Keep responses under 100 words.`,
      
      'es-ES': `Eres un asistente útil de pagos por voz para una aplicación de billetera de criptomonedas BNB Chain.
        Ayudas a los usuarios con:
        - Enviar BNB y otros tokens
        - Verificar saldos de billetera
        - Ver historial de transacciones
        - Entender transacciones blockchain
        
        Siempre sé conciso, útil y consciente de la seguridad.
        Nunca pidas claves privadas o frases semilla.
        Responde en un tono conversacional adecuado para interacción por voz.
        Mantén las respuestas bajo 100 palabras.`,
        
      'fr-FR': `Vous êtes un assistant de paiement vocal utile pour une application de portefeuille de cryptomonnaie BNB Chain.
        Vous aidez les utilisateurs avec:
        - Envoyer des BNB et autres tokens
        - Vérifier les soldes de portefeuille
        - Voir l'historique des transactions
        - Comprendre les transactions blockchain
        
        Soyez toujours concis, utile et soucieux de la sécurité.
        Ne demandez jamais de clés privées ou phrases de récupération.
        Répondez dans un ton conversationnel adapté à l'interaction vocale.
        Gardez les réponses sous 100 mots.`
    };
    
    const langCode = language.split('-')[0];
    return prompts[language] || prompts[`${langCode}-${langCode.toUpperCase()}`] || prompts['en-US'];
  }

  private getFallbackResponse(language: string): string {
    const fallbacks: Record<string, string> = {
      'en-US': 'I\'m here to help with your voice payments. You can ask me to send tokens, check balances, or view transaction history.',
      'es-ES': 'Estoy aquí para ayudar con tus pagos por voz. Puedes pedirme que envíe tokens, revise saldos o vea el historial de transacciones.',
      'fr-FR': 'Je suis là pour vous aider avec vos paiements vocaux. Vous pouvez me demander d\'envoyer des tokens, vérifier les soldes ou voir l\'historique des transactions.'
    };
    
    const langCode = language.split('-')[0];
    return fallbacks[language] || fallbacks[`${langCode}-${langCode.toUpperCase()}`] || fallbacks['en-US'];
  }

  async processVoiceCommand(command: string, language: string = 'en-US'): Promise<{
    response: string;
    action?: string;
    parameters?: any;
  }> {
    try {
      const enhancedPrompt = `
        User voice command: "${command}"
        
        Analyze this command and provide:
        1. A helpful response
        2. If it's a payment command, extract: action, amount, token, recipient
        
        Respond in JSON format:
        {
          "response": "your helpful response",
          "action": "send|check|history|other",
          "parameters": {
            "amount": "number or null",
            "token": "token symbol or null",
            "recipient": "address or null"
          }
        }
      `;
      
      const llmResponse = await this.generateResponse(enhancedPrompt, language);
      
      try {
        const parsed = JSON.parse(llmResponse);
        return parsed;
      } catch {
        // If JSON parsing fails, return simple response
        return {
          response: llmResponse,
          action: 'other'
        };
      }
    } catch (error) {
      console.error('Voice command processing error:', error);
      return {
        response: this.getFallbackResponse(language),
        action: 'other'
      };
    }
  }
}

export const llmService = new LLMService();
export default llmService;